
import React, { useState } from 'react';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuthUser } from '@/hooks/superadmin/useAuthUser';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Wallet, CreditCard, RefreshCw, ShoppingCart, ArrowRightLeft } from 'lucide-react';
import AdminWallet from '@/components/masterlicense/wallet/AdminWallet';
import RewardInvoices from '@/components/masterlicense/wallet/RewardInvoices';
import WalletTransferDialog from '@/components/masterlicense/wallet/WalletTransferDialog';
import TremendousRewardSalesReport from '@/components/masterlicense/tremendous/TremendousRewardSalesReport';
import WalletRecentCharges from '@/components/masterlicense/wallet/WalletRecentCharges';

const SuperAdminRewardSales: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, handleLogout: authUserLogout } = useAuthUser();
  
  const [activeTab, setActiveTab] = useState('wallet');
  const [isLoading, setIsLoading] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      console.log("Super Admin logout initiated");
      
      await authUserLogout();
      
      toast({
        title: "Logged out",
        description: "You have been successfully logged out."
      });
      
      // Navigate to login page after logout
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshWallet = (balance: number) => {
    setCurrentBalance(balance);
  };

  return (
    <SuperAdminLayout
      isLoading={isLoading}
      onLogout={handleLogout}
      userRole={user?.role || 'superadmin'}
    >
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <h1 className="text-2xl font-bold">Reward Sales Management</h1>
          {activeTab === 'wallet' && (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setTransferDialogOpen(true)}
                className="flex items-center"
              >
                <ArrowRightLeft className="mr-2 h-4 w-4" />
                Transfer Funds
              </Button>
            </div>
          )}
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="wallet">
              <Wallet className="mr-2 h-4 w-4" />
              Wallet & Charges
            </TabsTrigger>
            <TabsTrigger value="report">
              <RefreshCw className="mr-2 h-4 w-4" />
              Sales Reports
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="wallet">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <AdminWallet 
                adminId={user?.id || ""} 
                onBalanceUpdate={handleRefreshWallet}
              />
              <WalletRecentCharges adminId={user?.id || ""} />
            </div>
            <RewardInvoices adminId={user?.id || ""} />
          </TabsContent>
          
          <TabsContent value="report">
            <TremendousRewardSalesReport />
          </TabsContent>
        </Tabs>
        
        {transferDialogOpen && (
          <WalletTransferDialog
            open={transferDialogOpen}
            onClose={() => setTransferDialogOpen(false)}
            onSuccess={() => {/* Handle success */}}
            currentBalance={currentBalance}
            transferToId="master-admin-id"
            transferToName="Master Admin Account"
          />
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminRewardSales;
